import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { daysheetAPI } from '../services/api';
import Layout from '../components/Layout';
import './DaysheetDetails.css';

const Icon = ({ name, size = 18 }) => {
    const icons = {
        calendar: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect><line x1="16" y1="2" x2="16" y2="6"></line><line x1="8" y1="2" x2="8" y2="6"></line><line x1="3" y1="10" x2="21" y2="10"></line></svg>,
        user: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path><circle cx="12" cy="7" r="4"></circle></svg>,
        clock: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"></circle><polyline points="12 6 12 12 16 14"></polyline></svg>,
        mapPin: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path><circle cx="12" cy="10" r="3"></circle></svg>,
        alert: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"></circle><line x1="12" y1="8" x2="12" y2="12"></line><line x1="12" y1="16" x2="12.01" y2="16"></line></svg>,
        arrowLeft: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><line x1="19" y1="12" x2="5" y2="12"></line><polyline points="12 19 5 12 12 5"></polyline></svg>,
        check: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"></polyline></svg>,
        code: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="16 18 22 12 16 6"></polyline><polyline points="8 6 2 12 8 18"></polyline></svg>,
        coffee: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M18 8h1a4 4 0 0 1 0 8h-1"></path><path d="M2 8h16v9a4 4 0 0 1-4 4H6a4 4 0 0 1-4-4V8z"></path><line x1="6" y1="1" x2="6" y2="4"></line><line x1="10" y1="1" x2="10" y2="4"></line><line x1="14" y1="1" x2="14" y2="4"></line></svg>
    };
    return icons[name] || null;
};

const DaysheetDetails = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [daysheet, setDaysheet] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        fetchDetails();
    }, [id]);

    const fetchDetails = async () => {
        try {
            setLoading(true);
            const res = await daysheetAPI.getById(id);
            setDaysheet(res.data.data);
        } catch (err) {
            console.error('Fetch error:', err);
        } finally {
            setLoading(false);
        }
    };

    const formatTime = (time) => {
        if (!time) return '--:--';
        return new Date(time).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
    };

    if (loading) return (
        <Layout>
            <div className="daysheet-details-loading">
                <div className="spinner"></div>
                <p>Loading record details...</p>
            </div>
        </Layout>
    );

    if (!daysheet) return (
        <Layout>
            <div className="daysheet-details-error">
                <h3>Daysheet not found</h3>
                <button onClick={() => navigate('/daysheet')} className="back-link">
                    <Icon name="arrowLeft" /> Back to List
                </button>
            </div>
        </Layout>
    );

    return (
        <Layout>
            <div className="daysheet-details-page">
                <header className="sticky-header">
                    <div className="header-left">
                        <button className="back-to-list" onClick={() => navigate('/daysheet')}>
                            <Icon name="arrowLeft" />
                            <span>Back to Daysheets</span>
                        </button>
                        <h2 className="page-title">Daysheet &ndash; {new Date(daysheet.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</h2>
                    </div>
                    <div className="header-right">
                        <span className={`status-badge-pill ${daysheet.status.toLowerCase()}`}>
                            {daysheet.status}
                        </span>
                    </div>
                </header>

                <div className="details-main-grid">
                    {/* Column 1: Employee Summary */}
                    <aside className="col-employee-summary">
                        <div className="details-card profile-card">
                            <div className="avatar-wrapper">
                                <div className="avatar-letter">{daysheet.userName?.charAt(0)}</div>
                            </div>
                            <div className="profile-info">
                                <h3>{daysheet.userName}</h3>
                                <span className="role-text">{daysheet.role}</span>
                            </div>
                            <div className="summary-date-box">
                                <Icon name="calendar" size={14} />
                                <span>{new Date(daysheet.date).toLocaleDateString('en-US', { weekday: 'long' })}</span>
                            </div>
                        </div>
                    </aside>

                    {/* Column 2: Primary Content */}
                    <main className="col-primary-content">
                        <section className="details-card content-card">
                            <div className="card-header">
                                <div className="card-title-line">
                                    <Icon name="check" size={16} />
                                    <h4>Work Accomplishment</h4>
                                </div>
                            </div>
                            <div className="summary-text">
                                {daysheet.workSummary}
                            </div>
                        </section>

                        {daysheet.blockers && (
                            <section className="details-card content-card blocker-card">
                                <div className="card-header">
                                    <div className="card-title-line">
                                        <Icon name="alert" size={16} />
                                        <h4>Blockers & Issues</h4>
                                    </div>
                                </div>
                                <div className="summary-text-blocker">
                                    {daysheet.blockers}
                                </div>
                            </section>
                        )}

                        {daysheet.tasksWorkedOn?.length > 0 && (
                            <section className="details-card content-card">
                                <div className="card-header">
                                    <div className="card-title-line">
                                        <Icon name="code" size={16} />
                                        <h4>Linked Tasks</h4>
                                    </div>
                                </div>
                                <div className="tasks-flow">
                                    {daysheet.tasksWorkedOn.map((t, i) => (
                                        <div key={i} className="task-item-pill">
                                            <span className="task-title">{t.taskTitle}</span>
                                            <span className="task-time">{(t.timeSpent / 60).toFixed(1)}h</span>
                                        </div>
                                    ))}
                                </div>
                            </section>
                        )}

                        {daysheet.commits && (
                            <section className="details-card content-card">
                                <div className="card-header">
                                    <div className="card-title-line">
                                        <Icon name="code" size={16} />
                                        <h4>Commits & References</h4>
                                    </div>
                                </div>
                                <div className="commit-ref-box">
                                    {daysheet.commits}
                                </div>
                            </section>
                        )}

                        {daysheet.tomorrowPlan && (
                            <section className="details-card content-card tomorrow-card">
                                <div className="card-header">
                                    <div className="card-title-line">
                                        <Icon name="calendar" size={16} />
                                        <h4>Plan for Tomorrow</h4>
                                    </div>
                                </div>
                                <div className="summary-text-tomorrow">
                                    {daysheet.tomorrowPlan}
                                </div>
                            </section>
                        )}
                    </main>

                    {/* Column 3: Meta Info */}
                    <aside className="col-meta-info">
                        <div className="details-card meta-card">
                            <div className="meta-list">
                                <div className="meta-item">
                                    <div className="meta-icon"><Icon name="clock" /></div>
                                    <div className="meta-content">
                                        <label>Total Hours</label>
                                        <span>{daysheet.totalWorkingHours.toFixed(2)} hrs</span>
                                    </div>
                                </div>

                                <div className="meta-item">
                                    <div className="meta-icon"><Icon name="mapPin" /></div>
                                    <div className="meta-content">
                                        <label>Work Location</label>
                                        <span>{daysheet.workLocation}</span>
                                    </div>
                                </div>

                                <div className="punch-details-grid">
                                    <div className="punch-mini-card">
                                        <label>Punch In</label>
                                        <span>{formatTime(daysheet.punchInTime)}</span>
                                    </div>
                                    <div className="punch-mini-card">
                                        <label>Punch Out</label>
                                        <span>{formatTime(daysheet.punchOutTime)}</span>
                                    </div>
                                </div>

                                {daysheet.breakDuration && (
                                    <div className="meta-item">
                                        <div className="meta-icon"><Icon name="coffee" /></div>
                                        <div className="meta-content">
                                            <label>Break Taken</label>
                                            <span>{daysheet.breakDuration} mins</span>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>

                        {daysheet.status === 'Edited' && (
                            <div className="details-card audit-card">
                                <div className="card-header-mini">
                                    <Icon name="alert" size={14} />
                                    <span>Audit Trail</span>
                                </div>
                                <div className="audit-info">
                                    <p><strong>Edited by:</strong> {daysheet.editedBy?.name || 'Manager'}</p>
                                    <p><strong>Reason:</strong> {daysheet.editReason}</p>
                                </div>
                            </div>
                        )}
                    </aside>
                </div>
            </div>
        </Layout>
    );
};

export default DaysheetDetails;
